import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/auth';
import { useAuthStore } from '../lib/auth';
import { turkishIncludes } from '../lib/turkishSearch';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Inbox, Send, Archive, Trash2, Mail, Plus, Search, Loader2,
  Paperclip, ArrowLeft, MailOpen, X, RefreshCw, User,
  Download, ChevronRight, Clock, Users, Eye,
  PanelRightClose, PanelBottomClose, Reply
} from 'lucide-react';

const FOLDERS = [
  { key: 'inbox', label: 'Gelen Kutusu', icon: Inbox, color: 'text-blue-500' },
  { key: 'sent', label: 'Gönderilenler', icon: Send, color: 'text-emerald-500' },
  { key: 'archived', label: 'Arşiv', icon: Archive, color: 'text-amber-500' },
  { key: 'deleted', label: 'Silinen Öğeler', icon: Trash2, color: 'text-red-400' },
];

export default function Messages() {
  const { user } = useAuthStore();
  const [folder, setFolder] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [folderCounts, setFolderCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // reply prefill data
  const [searchQuery, setSearchQuery] = useState('');
  const [layoutMode, setLayoutMode] = useState(() => localStorage.getItem('msg_layout') || 'side');
  const [mobileSection, setMobileSection] = useState('folders'); // 'folders' | 'list' | 'detail'
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchFolders = useCallback(async () => {
    try {
      const res = await api.get('/messages/folders');
      if (res.data?.success) setFolderCounts(res.data.data);
    } catch {}
  }, []);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/messages/${folder}?page=1&per_page=100`);
      if (res.data?.success) setMessages(res.data.data || []);
    } catch {} finally { setLoading(false); }
  }, [folder]);

  // Listen to WebSocket events dispatched from DashboardLayout
  useEffect(() => {
    const handleNewMessage = (e) => {
      const msg = e.detail;
      if (folder === 'inbox') {
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          return [{
            id: msg.id,
            sender_id: msg.sender_id,
            sender_name: msg.sender_name,
            subject: msg.subject,
            body: msg.body_preview || '',
            created_at: msg.created_at,
            attachment_count: msg.attachment_count || 0,
            is_read: false,
            recipients: []
          }, ...prev];
        });
      }
      fetchFolders();
      toast.info(`Yeni mesaj: ${msg.sender_name}`, { description: msg.subject });
    };

    const handleReadReceipt = (e) => {
      const data = e.detail;
      setSelectedMsg(prev => {
        if (!prev || prev.id !== data.message_id) return prev;
        return {
          ...prev,
          recipients: prev.recipients.map(r =>
            r.user_id === data.reader_id ? { ...r, read: true, read_at: data.read_at } : r
          )
        };
      });
      setMessages(prev => prev.map(m => {
        if (m.id !== data.message_id) return m;
        return {
          ...m,
          recipients: m.recipients?.map(r =>
            r.user_id === data.reader_id ? { ...r, read: true, read_at: data.read_at } : r
          )
        };
      }));
    };

    window.addEventListener('ws:new_message', handleNewMessage);
    window.addEventListener('ws:read_receipt', handleReadReceipt);
    return () => {
      window.removeEventListener('ws:new_message', handleNewMessage);
      window.removeEventListener('ws:read_receipt', handleReadReceipt);
    };
  }, [folder, fetchFolders]);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);
  useEffect(() => { fetchMessages(); setSelectedMsg(null); }, [fetchMessages]);

  const openMessage = async (msgId) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/messages/detail/${msgId}`);
      if (res.data?.success) {
        setSelectedMsg(res.data.data);
        setMobileSection('detail');
        fetchFolders();
        // Update local read status
        setMessages(prev => prev.map(m => m.id === msgId ? { ...m, is_read: true } : m));
      }
    } catch { toast.error('Mesaj açılamadı'); } finally { setDetailLoading(false); }
  };

  const doAction = async (msgId, action) => {
    try {
      await api.put(`/messages/${msgId}/action`, { action });
      if (action === 'delete' || action === 'archive') {
        setMessages(prev => prev.filter(m => m.id !== msgId));
        if (selectedMsg?.id === msgId) setSelectedMsg(null);
      }
      fetchFolders();
      toast.success(action === 'delete' ? 'Silindi' : action === 'archive' ? 'Arşivlendi' : action === 'restore' ? 'Geri alındı' : 'Güncellendi');
      if (action === 'restore') fetchMessages();
    } catch { toast.error('İşlem hatası'); }
  };

  const filtered = messages.filter(m => {
    if (!searchQuery) return true;
    return turkishIncludes(m.subject, searchQuery) || turkishIncludes(m.sender_name, searchQuery) || turkishIncludes(m.body, searchQuery);
  });

  // Reply handler
  const handleReply = () => {
    if (!selectedMsg) return;
    setReplyTo({
      recipients: [selectedMsg.sender_id],
      subject: selectedMsg.subject?.startsWith('Re: ') ? selectedMsg.subject : `Re: ${selectedMsg.subject}`,
      body: `<br/><br/><hr/><p style="color:#666;font-size:12px"><strong>${selectedMsg.sender_name}</strong> — ${new Date(selectedMsg.created_at).toLocaleString('tr-TR')}</p><blockquote style="border-left:3px solid #ddd;padding-left:12px;margin-left:0;color:#666">${selectedMsg.body}</blockquote>`
    });
    setComposeOpen(true);
  };

  // Mobile: show only sidebar, list, or detail based on state
  const mobileView = selectedMsg ? 'detail' : 'list';

  return (
    <div className="flex h-[calc(100vh-120px)] gap-0 md:gap-4" data-testid="messages-page">
      {/* Sidebar - hidden on mobile when viewing list or detail */}
      <div className={`${mobileSection !== 'folders' ? 'hidden md:block' : ''} w-full md:w-56 shrink-0 space-y-2`}>
        <Button className="w-full gap-2" onClick={() => { setReplyTo(null); setComposeOpen(true); }} data-testid="compose-btn">
          <Plus className="h-4 w-4" /> Yeni Mesaj
        </Button>
        <div className="space-y-0.5 mt-3">
          {FOLDERS.map(f => {
            const Icon = f.icon;
            const count = f.key === 'inbox' ? folderCounts?.inbox?.unread : folderCounts?.[f.key]?.total;
            return (
              <button
                key={f.key}
                onClick={() => { setFolder(f.key); setMobileSection('list'); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${folder === f.key ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-semibold' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'}`}
                data-testid={`folder-${f.key}`}
              >
                <Icon className={`h-4 w-4 ${f.color}`} />
                <span className="flex-1 text-left">{f.label}</span>
                {count > 0 && (
                  <Badge variant={f.key === 'inbox' ? 'default' : 'secondary'} className={`text-[10px] h-5 min-w-[20px] justify-center ${f.key === 'inbox' ? 'bg-blue-500' : ''}`}>
                    {count}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Message list + detail */}
      <div className={`flex-1 flex ${layoutMode === 'bottom' ? 'flex-col' : 'flex-row'} gap-0 md:gap-3 min-w-0 ${mobileSection === 'folders' ? 'hidden md:flex' : 'flex'}`}>
        {/* Message list - hidden on mobile when detail is open */}
        <Card className={`${mobileSection === 'detail' ? 'hidden md:flex' : 'flex'} ${selectedMsg && layoutMode === 'side' ? 'md:w-[360px] md:shrink-0' : ''} flex-col flex-1 ${selectedMsg ? 'md:flex-none' : ''} ${!selectedMsg ? 'md:flex-1' : ''} ${layoutMode === 'bottom' && selectedMsg ? 'max-h-[40%]' : ''}`}>
          {/* Back to folders button on mobile */}
          <div className="md:hidden px-3 py-2 border-b border-slate-100 dark:border-slate-800">
            <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => setMobileSection('folders')}>
              <ArrowLeft className="h-3 w-3" /> Klasörler
            </Button>
          </div>
          <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input placeholder="Ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-7 h-8 text-sm" />
            </div>
            {/* Layout toggle */}
            <div className="flex bg-slate-100 dark:bg-slate-800 rounded-md p-0.5">
              <button onClick={() => { setLayoutMode('side'); localStorage.setItem('msg_layout', 'side'); }} className={`p-1 rounded transition-colors ${layoutMode === 'side' ? 'bg-white dark:bg-slate-700 shadow-sm' : 'text-slate-400'}`} title="Yan panel">
                <PanelRightClose className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => { setLayoutMode('bottom'); localStorage.setItem('msg_layout', 'bottom'); }} className={`p-1 rounded transition-colors ${layoutMode === 'bottom' ? 'bg-white dark:bg-slate-700 shadow-sm' : 'text-slate-400'}`} title="Alt panel">
                <PanelBottomClose className="h-3.5 w-3.5" />
              </button>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { fetchMessages(); fetchFolders(); }}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Mail className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Mesaj yok</p>
              </div>
            ) : (
              filtered.map(msg => (
                <button
                  key={msg.id}
                  onClick={() => openMessage(msg.id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-slate-50 dark:border-slate-800 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50 ${selectedMsg?.id === msg.id ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''} ${!msg.is_read ? 'bg-blue-50/30' : ''}`}
                  data-testid={`msg-${msg.id}`}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    {!msg.is_read && <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                    <span className={`text-sm truncate flex-1 ${!msg.is_read ? 'font-semibold text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}>
                      {folder === 'sent' ? msg.recipients?.map(r => r.name).join(', ') : msg.sender_name}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0">
                      {new Date(msg.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}
                    </span>
                  </div>
                  <p className={`text-xs truncate ${!msg.is_read ? 'font-medium text-slate-700 dark:text-slate-200' : 'text-slate-500'}`}>{msg.subject}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {msg.attachment_count > 0 && <Paperclip className="h-2.5 w-2.5 text-slate-400" />}
                    {folder === 'sent' && msg.recipients?.every(r => r.read) && (
                      <span className="flex items-center gap-0.5 text-[10px] text-emerald-500"><Eye className="h-2.5 w-2.5" />Okundu</span>
                    )}
                    {folder === 'sent' && msg.recipients?.some(r => !r.read) && (
                      <span className="flex items-center gap-0.5 text-[10px] text-slate-400"><MailOpen className="h-2.5 w-2.5" /></span>
                    )}
                    <p className="text-[10px] text-slate-400 truncate">{msg.body?.replace(/<[^>]*>/g, '').slice(0, 60)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </Card>

        {/* Message detail */}
        {selectedMsg && (
          <Card className="flex-1 flex flex-col min-w-0">
            {detailLoading ? (
              <div className="flex-1 flex justify-center items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : (
              <>
                <div className="px-3 md:px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center justify-between mb-2">
                    <Button variant="ghost" size="sm" className="h-7 gap-1" onClick={() => { setSelectedMsg(null); setMobileSection('list'); }}>
                      <ArrowLeft className="h-3 w-3" /> Geri
                    </Button>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" className="h-7 gap-1 text-blue-600 border-blue-200 hover:bg-blue-50" onClick={handleReply} data-testid="reply-btn">
                        <Reply className="h-3 w-3" /> Yanıtla
                      </Button>
                      {folder !== 'archived' && folder !== 'deleted' && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => doAction(selectedMsg.id, 'archive')} title="Arşivle">
                          <Archive className="h-3.5 w-3.5 text-amber-500" />
                        </Button>
                      )}
                      {folder !== 'deleted' && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => doAction(selectedMsg.id, 'delete')} title="Sil">
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      )}
                      {(folder === 'deleted' || folder === 'archived') && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => doAction(selectedMsg.id, 'restore')}>
                          <RefreshCw className="h-3 w-3" /> Geri Al
                        </Button>
                      )}
                    </div>
                  </div>
                  <h2 className="text-base md:text-lg font-bold text-slate-800 dark:text-slate-100">{selectedMsg.subject}</h2>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                        <User className="h-3.5 w-3.5 text-blue-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-700 dark:text-slate-200">{selectedMsg.sender_name}</p>
                        <p className="text-[10px]">{selectedMsg.sender_email}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-3 w-3 text-slate-300" />
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {selectedMsg.recipients?.map(r => (
                        <div key={r.user_id} className="flex items-center gap-1">
                          <Badge variant={r.read ? 'default' : 'secondary'} className={`text-[10px] gap-1 ${r.read ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : ''}`}>
                            {r.name}
                            {r.read ? <Eye className="h-2.5 w-2.5" /> : <MailOpen className="h-2.5 w-2.5 opacity-40" />}
                          </Badge>
                          {r.read && r.read_at && (
                            <span className="text-[9px] text-emerald-500 whitespace-nowrap">
                              {new Date(r.read_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    <span className="ml-auto flex items-center gap-1 shrink-0">
                      <Clock className="h-3 w-3" />
                      {new Date(selectedMsg.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  {/* HTML body render */}
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none text-slate-700 dark:text-slate-300"
                    dangerouslySetInnerHTML={{ __html: selectedMsg.body }}
                  />

                  {/* Read status log */}
                  {selectedMsg.recipients?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                      <p className="text-xs text-slate-500 mb-2 flex items-center gap-1"><Eye className="h-3 w-3" /> Okunma Durumu</p>
                      <div className="space-y-1.5">
                        {selectedMsg.recipients.map(r => (
                          <div key={r.user_id} className="flex items-center gap-2 text-xs">
                            <div className={`w-2 h-2 rounded-full shrink-0 ${r.read ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                            <span className="font-medium text-slate-700 dark:text-slate-200 min-w-[120px]">{r.name}</span>
                            {r.read ? (
                              <span className="text-emerald-600 flex items-center gap-1">
                                <Eye className="h-3 w-3" />
                                Görüldü — {r.read_at ? new Date(r.read_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                              </span>
                            ) : (
                              <span className="text-slate-400 flex items-center gap-1">
                                <MailOpen className="h-3 w-3" /> Henüz okunmadı
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedMsg.attachments?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                      <p className="text-xs text-slate-500 mb-2 flex items-center gap-1"><Paperclip className="h-3 w-3" /> Ekler ({selectedMsg.attachments.length})</p>
                      <div className="flex gap-2 flex-wrap">
                        {selectedMsg.attachments.map((att, idx) => (
                          <a
                            key={idx}
                            href={`${API_URL}/api/messages/attachment/${selectedMsg.id}/${idx}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:border-blue-300 bg-slate-50 dark:bg-slate-800 transition-colors"
                          >
                            <Paperclip className="h-3.5 w-3.5 text-slate-400" />
                            <div>
                              <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{att.file_name}</p>
                              <p className="text-[10px] text-slate-400">{(att.file_size / 1024).toFixed(0)} KB</p>
                            </div>
                            <Download className="h-3 w-3 text-blue-500" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </Card>
        )}
      </div>

      {/* Compose Dialog */}
      <ComposeDialog
        open={composeOpen}
        onClose={() => { setComposeOpen(false); setReplyTo(null); }}
        user={user}
        replyTo={replyTo}
        onSent={() => { setComposeOpen(false); setReplyTo(null); fetchMessages(); fetchFolders(); }}
      />
    </div>
  );
}

// ========== COMPOSE DIALOG ==========
function ComposeDialog({ open, onClose, user, onSent, replyTo }) {
  const [recipients, setRecipients] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const fileRef = useRef(null);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    if (!open) return;
    // If replying, prefill
    if (replyTo) {
      setRecipients(replyTo.recipients || []);
      setSubject(replyTo.subject || '');
      setBody(replyTo.body || '');
    } else {
      setRecipients([]);
      setSubject('');
      setBody('');
    }
    setAttachments([]);
    setUserSearch('');
    fetchRecipients();
    if (isSuperAdmin) fetchFirms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && isSuperAdmin) fetchRecipients(selectedFirm);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFirm]);

  const fetchRecipients = async (firmId = '') => {
    setLoadingUsers(true);
    try {
      const res = await api.get(`/messages/recipients/list?firm_id=${firmId}`);
      if (res.data?.success) setAvailableUsers(res.data.data || []);
    } catch {} finally { setLoadingUsers(false); }
  };

  const fetchFirms = async () => {
    try {
      const res = await api.get('/firms');
      if (res.data?.success) setFirms(res.data.data || []);
    } catch {}
  };

  const toggleRecipient = (userId) => {
    setRecipients(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const selectAll = () => {
    const visible = filteredUsers.map(u => u.id);
    const allSelected = visible.every(id => recipients.includes(id));
    if (allSelected) {
      setRecipients(prev => prev.filter(id => !visible.includes(id)));
    } else {
      setRecipients(prev => [...new Set([...prev, ...visible])]);
    }
  };

  const handleSend = async () => {
    if (recipients.length === 0) { toast.error('En az bir alıcı seçiniz'); return; }
    if (!subject.trim()) { toast.error('Konu giriniz'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('recipients', recipients.join(','));
      fd.append('subject', subject.trim());
      fd.append('body', body.trim());
      attachments.forEach(f => fd.append('files', f));
      await api.post('/messages', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Mesaj gönderildi');
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gönderme hatası');
    } finally { setSending(false); }
  };

  const filteredUsers = availableUsers.filter(u => {
    if (!userSearch) return true;
    return turkishIncludes(u.full_name, userSearch) || turkishIncludes(u.email, userSearch);
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-500" /> Yeni Mesaj
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Firm selector (Super Admin) */}
          {isSuperAdmin && (
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Firma</Label>
              <Select value={selectedFirm || 'all'} onValueChange={v => setSelectedFirm(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9" data-testid="compose-firm-select">
                  <SelectValue placeholder="Tüm Firmalar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Firmalar</SelectItem>
                  {firms.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Recipients */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Alıcılar * ({recipients.length} seçili)</Label>
            {recipients.length > 0 && (
              <div className="flex gap-1 flex-wrap mb-2">
                {recipients.map(rid => {
                  const u = availableUsers.find(u => u.id === rid);
                  return u ? (
                    <Badge key={rid} variant="secondary" className="text-xs gap-1">
                      {u.full_name}
                      <button onClick={() => toggleRecipient(rid)} className="ml-0.5 hover:text-red-500"><X className="h-2.5 w-2.5" /></button>
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
            <div className="flex gap-2 mb-1.5">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input placeholder="Kişi ara..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className="pl-8 h-8 text-sm" />
              </div>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={selectAll}>
                <Users className="h-3 w-3 mr-1" /> {filteredUsers.every(u => recipients.includes(u.id)) ? 'Hiçbirini Seçme' : 'Tümünü Seç'}
              </Button>
            </div>
            <div className="border rounded-lg max-h-32 overflow-y-auto">
              {loadingUsers ? (
                <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" /></div>
              ) : filteredUsers.length === 0 ? (
                <p className="text-center py-4 text-xs text-slate-400">Kişi bulunamadı</p>
              ) : (
                filteredUsers.map(u => (
                  <div
                    key={u.id}
                    onClick={() => toggleRecipient(u.id)}
                    className={`flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${recipients.includes(u.id) ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''}`}
                  >
                    <Checkbox checked={recipients.includes(u.id)} className="h-3.5 w-3.5" />
                    <span className="text-sm flex-1">{u.full_name}</span>
                    <span className="text-[10px] text-slate-400">{u.role}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Subject */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Konu *</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Mesaj konusu..." data-testid="compose-subject" />
          </div>

          {/* Body - Rich Text Editor */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Mesaj</Label>
            <RichTextEditor value={body} onChange={setBody} />
          </div>

          {/* Attachments */}
          <div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => fileRef.current?.click()}>
                <Paperclip className="h-3 w-3" /> Dosya Ekle
              </Button>
              <span className="text-[10px] text-slate-400">Maks. 50MB</span>
              <input ref={fileRef} type="file" multiple className="hidden" onChange={e => { setAttachments(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }} />
            </div>
            {attachments.length > 0 && (
              <div className="flex gap-1.5 flex-wrap mt-2">
                {attachments.map((f, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs gap-1">
                    <Paperclip className="h-2.5 w-2.5" /> {f.name?.slice(0, 20)} ({(f.size / 1024).toFixed(0)}KB)
                    <button onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}><X className="h-2.5 w-2.5 text-red-400" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={handleSend} disabled={sending} data-testid="send-message-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Gönder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ========== RICH TEXT EDITOR ==========
import {
  Bold, Italic, Underline, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Link2, Type, Strikethrough
} from 'lucide-react';

function RichTextEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const isInitRef = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInitRef.current && value) {
      editorRef.current.innerHTML = value;
      isInitRef.current = true;
    }
  }, [value]);

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    handleInput();
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const insertLink = () => {
    const url = prompt('URL giriniz:');
    if (url) exec('createLink', url);
  };

  const ToolBtn = ({ onClick, active, children, title }) => (
    <button
      type="button"
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      className={`p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors ${active ? 'bg-slate-200 dark:bg-slate-700' : ''}`}
      title={title}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden" data-testid="compose-body">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex-wrap">
        <select
          className="h-7 text-xs border border-slate-200 rounded px-1 bg-white dark:bg-slate-900 dark:border-slate-700"
          onChange={e => { if (e.target.value) exec('formatBlock', e.target.value); e.target.value = ''; }}
          defaultValue=""
        >
          <option value="" disabled>Başlık</option>
          <option value="h1">Başlık 1</option>
          <option value="h2">Başlık 2</option>
          <option value="h3">Başlık 3</option>
          <option value="p">Normal</option>
        </select>
        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
        <ToolBtn onClick={() => exec('bold')} title="Kalın"><Bold className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('italic')} title="İtalik"><Italic className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('underline')} title="Altı Çizili"><Underline className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('strikeThrough')} title="Üstü Çizili"><Strikethrough className="h-3.5 w-3.5" /></ToolBtn>
        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
        <ToolBtn onClick={() => exec('insertUnorderedList')} title="Madde İşareti"><List className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('insertOrderedList')} title="Numaralı Liste"><ListOrdered className="h-3.5 w-3.5" /></ToolBtn>
        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
        <ToolBtn onClick={() => exec('justifyLeft')} title="Sola Hizala"><AlignLeft className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('justifyCenter')} title="Ortala"><AlignCenter className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('justifyRight')} title="Sağa Hizala"><AlignRight className="h-3.5 w-3.5" /></ToolBtn>
        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
        <ToolBtn onClick={insertLink} title="Bağlantı"><Link2 className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('removeFormat')} title="Biçimi Temizle"><Type className="h-3.5 w-3.5" /></ToolBtn>
      </div>
      {/* Editor area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="min-h-[140px] max-h-[300px] overflow-y-auto px-3 py-2 text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 focus:outline-none prose prose-sm dark:prose-invert max-w-none"
        style={{ wordBreak: 'break-word' }}
        data-placeholder="Mesajınızı yazın..."
      />
    </div>
  );
}
